/**
 * Parse a SPA-formatted price string (e.g. "$19.99", "€19,99", "1,234.56")
 * into a plain "19.99"-shaped numeric string suitable for Klaviyo's $value /
 * Price / ItemPrice fields.
 */
export const parsePriceToNumeric = (input: any): string => {
  if (input === null || input === undefined) return "0.00";
  if (typeof input === "number" && isFinite(input)) return input.toFixed(2);
  const str = String(input);
  if (!str) return "0.00";
  const cleaned = str.replace(/[^0-9.,-]/g, "");
  if (!cleaned) return "0.00";
  const lastDot = cleaned.lastIndexOf(".");
  const lastComma = cleaned.lastIndexOf(",");
  let normalized = cleaned;
  if (lastComma !== -1 && lastComma > lastDot) {
    normalized = cleaned.replace(/\./g, "").replace(",", ".");
  } else {
    normalized = cleaned.replace(/,/g, "");
  }
  const n = parseFloat(normalized);
  return isFinite(n) ? n.toFixed(2) : "0.00";
};

/**
 * Best-effort absolute URL builder. If a storefront URL was configured in
 * admin and forwarded via klaviyoSettings, prepend it; otherwise fall back
 * to window.location.origin so emails still get a full URL.
 *
 * Values that are ALREADY absolute are returned untouched. Catalog images
 * come back as `https://cdn.hooliganseyewear.com/...`, and prefixing those
 * produced `https://hooliganseyewear.com/https://cdn...` — a 404 in every
 * email block that renders a product image (Viewed Product, Viewed Category,
 * the "recently viewed" block). A protocol-relative `//host/...` is pinned to
 * https, because an email client has no page protocol to inherit.
 */
export const absoluteUrl = (
  storefrontUrl: string | undefined,
  path: string
): string => {
  const raw = (path || "").trim();
  // Protocol-relative only when the slashes are followed by something that can
  // actually be an authority AND a path: "//cdn.example.com/x.jpg" is an
  // address, while "//image/catalog/x.jpg", "//favicon.ico" and
  // "//index.php?route=…" are site paths that doubled their slash. Pinning any
  // of those to https would invent a host. A bracketed IPv6 literal carries no
  // dot, so it needs its own alternative.
  if (/^\/\/(\[[^\]\s/]+\]|[^/\s?#]+\.[^/\s?#]+)\//.test(raw)) {
    return `https:${raw}`;
  }
  if (/^https:/i.test(raw)) return raw;
  // The catalog is known to hand out http CDN URLs — see enforceHttpsUrl in
  // src/core/utils/seo.ts and its pinned case in tests/unit/jsonld-contract.
  // An http image in an email is the weak link this helper exists to remove.
  // Matched on the scheme alone, so the schemeless-slash forms browsers accept
  // ("http:/host/x", "http:host/x") are upgraded too rather than slipping past.
  if (/^http:/i.test(raw)) return raw.replace(/^http:/i, "https:");
  // data:, blob:, mailto: — not ours to rewrite, and prefixing them produced
  // the same 404 class this fix is for, one scheme over.
  if (/^[a-z][a-z0-9+.-]*:/i.test(raw)) return raw;
  // Collapse leading slashes, the same way normalizeFullPath does in
  // src/core/utils/seo.ts. A dotless "//image/catalog/x.jpg" reaches here from
  // the guard above, and leaving both slashes would emit either a duplicate,
  // non-canonical URL or — on the fallback branches below — a bare
  // protocol-relative address that no email client can resolve.
  const safePath = raw.startsWith("/") ? raw.replace(/^\/+/, "/") : `/${raw}`;
  // Trim before the emptiness check: a storefrontUrl of "/" is truthy but
  // carries no origin, and must still fall through to window.location.origin.
  const base = (storefrontUrl || "").trim().replace(/\/+$/, "");
  if (base) return base + safePath;
  if (
    typeof window !== "undefined" &&
    window.location &&
    window.location.origin
  ) {
    return window.location.origin + safePath;
  }
  return safePath;
};

const EMAIL_IMAGE_HOST = "cdn.hooliganseyewear.com";
const PNG_ORIGINAL_MARKER = /_png(?:[ _]\d+)?$/i;

const decodeSegment = (value: string) => {
  try {
    return decodeURIComponent(value);
  } catch (e) {
    return value;
  }
};

/**
 * Email clients such as Outlook for Windows do not render AVIF. Product
 * photos whose file name ends in "_PNG" (optionally " 2", "_2") were
 * uploaded with a PNG original next to the AVIF, so point emails at it.
 * Every other value is returned unchanged. The PHP event builders apply the
 * same rule (shared/opencart-tools/klaviyo-integration and marketing-tracking
 * in the workspace).
 */
export const emailSafeImageUrl = (url: string): string => {
  if (typeof url !== "string" || !url || url.trim() !== url) return url;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch (e) {
    return url;
  }
  if (
    !/^https?:$/.test(parsed.protocol) ||
    parsed.hostname !== EMAIL_IMAGE_HOST
  )
    return url;

  // Work on the raw string so the path keeps its original encoding.
  const base = url.slice(0, url.search(/[?#]|$/));
  const segment = base.slice(base.lastIndexOf("/") + 1);
  const dot = segment.lastIndexOf(".");
  if (dot === -1) return url;
  const extension = segment.slice(dot + 1);
  if (!/^(avif|webp)$/i.test(extension)) return url;
  if (!PNG_ORIGINAL_MARKER.test(decodeSegment(segment.slice(0, dot))))
    return url;
  return `${base.slice(0, base.length - extension.length)}png`;
};

export const safeParse = (value: string): any => {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch (e) {
    return null;
  }
};
